import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2, Play, Search, X, Filter, Tractor, Factory, Tag, Clock,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Weight, Gauge,
  Cog, SlidersHorizontal, RotateCcw, ExternalLink, Database, Wrench,
  Zap, Info, GitCompare, Fuel, ArrowUpDown, Droplets, Settings2,
  Sprout, Wheat, Scissors, Truck, Globe, Wifi, MapPin, Thermometer,
  LayoutGrid, List, Layers
} from "lucide-react";
import { authFetch, useAuth } from "@/hooks/use-auth";
import ModuleHeader from "@/components/ModuleHeader";

const FAB_COLORS: Record<string, string> = {
  "john-deere": "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30",
  "case-ih": "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
  "new-holland": "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
  "kuhn": "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30",
  "massey-ferguson": "bg-red-600/15 text-red-800 dark:text-red-300 border-red-600/30",
  "valtra": "bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30",
  "jacto": "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30",
  "stara": "bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500/30",
  "agrale": "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  "ls-tractor": "bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500/30",
  "mahindra": "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
  "semeato": "bg-lime-500/15 text-lime-700 dark:text-lime-400 border-lime-500/30",
  "baldan": "bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border-indigo-500/30",
  "jan": "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  "montana": "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
};

const SPEC_LABELS: Record<string, string> = {
  fonte: "Fonte",
  cilindrada_l: "Cilindrada (L)",
  num_cilindros: "Cilindros",
  aspiracao: "Aspiração",
  rpm_potencia_nominal: "RPM Pot. Nominal",
  torque_max_nm: "Torque Máx. (Nm)",
  reserva_torque_pct: "Reserva de Torque (%)",
  vazao_hidraulica_lpm: "Vazão Hidráulica (L/min)",
  valvulas_remotas: "Válvulas Remotas",
  tipo_tracao: "Tração",
  tomada_forca_rpm: "TDP (rpm)",
  levante_3p_kg: "Levante 3 pontos (kg)",
  levante_dianteiro_kg: "Levante Diant. (kg)",
  ar_condicionado: "Ar Condicionado",
  cabine: "Cabine",
  suspensao_cabine: "Suspensão Cabine",
  conectividade: "Conectividade",
  display: "Display",
  ipm: "IPM",
  potencia_ipm_cv: "Pot. com IPM (cv)",
  epm: "EPM",
  potencia_epm_cv: "Pot. com EPM (cv)",
  tanque_combustivel_l: "Tanque Comb. (L)",
  consumo_especifico_g_kwh: "Consumo Espec. (g/kWh)",
  tanque_graos_l: "Tanque de Grãos (L)",
  tanque_calda_l: "Tanque de Calda (L)",
  tanque_agua_limpa_l: "Tanque Água Limpa (L)",
  sistema_trilha: "Sistema de Trilha",
  area_peneiras_m2: "Área Peneiras (m²)",
  area_separacao_m2: "Área Separação (m²)",
  largura_plataforma_max_m: "Plataforma Máx. (m)",
  velocidade_max_kmh: "Vel. Máx. (km/h)",
  velocidade_descarga_lps: "Vel. Descarga (L/s)",
  rendimento_max_ton_h: "Rendimento (ton/h)",
  barra_max_m: "Barra Máx. (m)",
  vao_livre_m: "Vão Livre (m)",
  exactapply: "ExactApply",
  see_spray: "See & Spray",
  aim_command: "AIM Command",
  controle_bico_a_bico: "Controle Bico a Bico",
  controle_secao: "Controle de Seção",
  sistema_bico: "Sistema de Bicos",
  vazao_max_lpm: "Vazão Máx. (L/min)",
  linhas: "Nº de Linhas",
  espacamento_cm: "Espaçamento (cm)",
  sistema_dosagem: "Sistema de Dosagem",
  tipo_dosagem: "Tipo de Dosagem",
  singulacao_pct: "Singulação",
  capacidade_semente_l: "Cap. Semente (L)",
  capacidade_adubo_kg: "Cap. Adubo (kg)",
  controle_populacao: "Controle de População",
  ccs: "CCS",
  sensor_semente: "Sensor de Semente",
  monitor_semeadura: "Monitor Semeadura",
  taxa_variavel: "Taxa Variável",
  tipo_chassi: "Tipo de Chassi",
  tipo_disco: "Tipo de Disco",
  diametro_disco_mm: "Diâmetro Disco (mm)",
  diametro_disco_pol: "Diâmetro Disco (pol)",
  profundidade_max_cm: "Prof. Máx. (cm)",
  profundidade_min_cm: "Prof. Mín. (cm)",
  potencia_requerida_cv: "Potência Requerida (cv)",
  peso_por_disco_kg: "Peso por Disco (kg)",
  capacidade_m3: "Capacidade (m³)",
  capacidade_kg: "Capacidade (kg)",
  capacidade_tanque_l: "Cap. Tanque (L)",
  roscas: "Nº de Roscas",
  tipo_ferramenta: "Ferramenta",
  tipo_fardo: "Tipo de Fardo",
  dimensao_fardo_cm: "Dimensão Fardo (cm)",
  diametro_fardo_m: "Diâmetro Fardo (m)",
  diametro_min_m: "Diâmetro Mín. (m)",
  diametro_max_m: "Diâmetro Máx. (m)",
  sistema_amarre: "Sistema de Amarre",
  sistema_no: "Sistema de Nó",
  tipo_camara: "Tipo de Câmara",
  embalagem_plastico: "Embalagem Plástico",
  largura_coleta_m: "Largura Coleta (m)",
  sistema_colheita: "Sistema de Colheita",
  capacidade_sacas_h: "Capacidade (sacas/h)",
  desfolha_pct: "Desfolha",
  mudas_por_dia: "Mudas/dia",
  facas_corte_base: "Facas Corte Base",
  rolo_picador: "Rolo Picador",
  velocidade_colheita_kmh: "Vel. Colheita (km/h)",
  elevador: "Elevador",
  autotrac: "AutoTrac",
  starfire_receiver: "StarFire Receiver",
  precisao_rtk_cm: "Precisão RTK (cm)",
  piloto_automatico: "Piloto Automático",
  eixo_dianteiro: "Eixo Dianteiro",
  tipo_direcao: "Tipo de Direção",
  pressao_solo_kpa: "Pressão Solo (kPa)",
  opcao_quadtrac: "Quadtrac",
  opcao_smarttrax: "SmartTrax",
  peso_lastro_max_kg: "Lastro Máx. (kg)",
  creeper: "Creeper",
  carregador_frontal: "Carregador Frontal",
  peneiras_3d: "Peneiras 3D",
  combine_advisor: "Combine Advisor",
  active_yield: "Active Yield",
  mapa_produtividade: "Mapa Produtiv.",
  monitor: "Monitor",
  sistema_gestao: "Sistema de Gestão",
  tipo: "Tipo",
  aplicacoes: "Aplicações",
  aplicacao_principal: "Aplicação Principal",
  sistema_espalhamento: "Sist. Espalhamento",
  sistema_aplicacao: "Sist. Aplicação",
  correcao_automatica: "Correção Automática",
  controle_bordadura: "Controle Bordadura",
  pesagem_dinamica: "Pesagem Dinâmica",
  isobus: "ISOBUS",
  gps: "GPS",
  rtk: "RTK",
  mapa_aplicacao: "Mapa Aplicação",
  tela: "Tela",
  balanca: "Balança",
  descarga: "Descarga",
  discos: "Nº Discos",
  secoes: "Nº Seções",
  rotores: "Nº Rotores",
  condicionador: "Condicionador",
  largura_min_m: "Largura Mín. (m)",
  largura_max_m: "Largura Máx. (m)",
  largura_aplicacao_m: "Largura Aplic. (m)",
  largura_transporte_m: "Larg. Transporte (m)",
  hastes: "Nº Hastes",
  tipo_haste: "Tipo de Haste",
  tipo_navalha: "Tipo de Navalha",
  alimentacao: "Alimentação",
  corte_rente: "Corte Rente",
  velocidade_trabalho_kmh: "Vel. Trabalho (km/h)",
  acionamento: "Acionamento",
  sistema_abertura_sulco: "Abertura de Sulco",
  controle_profundidade: "Controle Prof.",
  turbina: "Turbina",
  peso_vazio_kg: "Peso Vazio (kg)",
  densidade_max_kg_m3: "Densidade Máx. (kg/m³)",
  bitola_min_m: "Bitola Mín. (m)",
  bitola_max_m: "Bitola Máx. (m)",
  comprimento_mm: "Comprimento (mm)",
  largura_mm: "Largura (mm)",
  largura_transporte_mm: "Larg. Transporte (mm)",
  altura_mm: "Altura (mm)",
  pneu_dianteiro: "Pneu Dianteiro",
  pneu_traseiro: "Pneu Traseiro",
  pneu: "Pneu",
};

function formatSpecValue(key: string, value: any): string {
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number") {
    if (key.includes("_l") && !key.includes("lpm") && !key.includes("lps")) return value.toLocaleString("pt-BR", { minimumFractionDigits: 1 });
    return value.toLocaleString("pt-BR");
  }
  return String(value);
}

function StatsCards({ stats }: { stats: any }) {
  if (!stats) return null;
  const coverage = stats.cobertura_dados;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
      <Card data-testid="card-stat-total">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Database className="h-3.5 w-3.5" /> Total</div>
          <p className="text-2xl font-bold">{stats.total_modelos?.toLocaleString("pt-BR")}</p>
        </CardContent>
      </Card>
      <Card data-testid="card-stat-fabricantes">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Factory className="h-3.5 w-3.5" /> Fabricantes</div>
          <p className="text-2xl font-bold">{stats.por_fabricante?.length || 0}</p>
        </CardContent>
      </Card>
      <Card data-testid="card-stat-categorias">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Tag className="h-3.5 w-3.5" /> Categorias</div>
          <p className="text-2xl font-bold">{stats.por_categoria?.length || 0}</p>
        </CardContent>
      </Card>
      {coverage && (
        <>
          <Card data-testid="card-stat-motor">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Cog className="h-3.5 w-3.5" /> Com Motor</div>
              <p className="text-2xl font-bold">{Math.round((coverage.com_motor / coverage.total) * 100)}%</p>
              <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${(coverage.com_motor / coverage.total) * 100}%` }}></div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="card-stat-peso">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Weight className="h-3.5 w-3.5" /> Com Peso</div>
              <p className="text-2xl font-bold">{Math.round((coverage.com_peso / coverage.total) * 100)}%</p>
              <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${(coverage.com_peso / coverage.total) * 100}%` }}></div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="card-stat-specs">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Settings2 className="h-3.5 w-3.5" /> Com Specs</div>
              <p className="text-2xl font-bold">{Math.round((coverage.com_specs_detalhados / coverage.total) * 100)}%</p>
              <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${(coverage.com_specs_detalhados / coverage.total) * 100}%` }}></div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

const CAT_ICONS: Record<string, any> = {
  "tratores": Tractor,
  "colheitadeiras": Wheat,
  "pulverizadores": Droplets,
  "plantadeiras": Sprout,
  "implementos": Wrench,
  "grades": Layers,
  "arados": Layers,
  "subsoladores": Layers,
  "distribuidores": Truck,
  "enfardadeiras": Tag,
  "segadeiras": Scissors,
  "misturadores": Cog,
  "roçadeiras": Scissors,
  "plataformas-de-corte": Scissors,
  "colhedoras-de-cana": Wheat,
};

function FabricantesGrid({ fabricantes, onSelect, selected }: { fabricantes: any[]; onSelect: (slug: string) => void; selected: string[] }) {
  if (!fabricantes?.length) return null;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-2">
      {fabricantes.filter((f: any) => parseInt(f.total_modelos) > 0).map((f: any) => (
        <Card
          key={f.slug}
          className={`border cursor-pointer transition-all hover:shadow-md ${FAB_COLORS[f.slug] || "bg-gray-500/10 border-gray-500/30"} ${selected.includes(f.slug) ? "ring-2 ring-primary shadow-md" : ""}`}
          onClick={() => onSelect(f.slug)}
          data-testid={`card-fab-${f.slug}`}
        >
          <CardContent className="pt-3 pb-2 px-2 text-center">
            <p className="font-semibold text-xs leading-tight truncate">{f.nome}</p>
            <Badge variant="secondary" className="mt-1 text-[10px] px-1.5">{parseInt(f.total_modelos)}</Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CategoriasChips({ categorias, selected, onSelect }: { categorias: any[]; selected: string[]; onSelect: (slug: string) => void }) {
  if (!categorias?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {categorias.filter((c: any) => parseInt(c.total_modelos) > 0).map((c: any) => {
        const IconComp = CAT_ICONS[c.slug] || Tag;
        const isActive = selected.includes(c.slug);
        return (
          <Button
            key={c.slug}
            variant={isActive ? "default" : "outline"}
            size="sm"
            className={`gap-1.5 h-7 text-xs ${isActive ? "" : "opacity-80 hover:opacity-100"}`}
            onClick={() => onSelect(c.slug)}
            data-testid={`chip-cat-${c.slug}`}
          >
            <IconComp className="h-3 w-3" />
            {c.nome}
            <span className="text-[10px] opacity-70">({c.total_modelos})</span>
          </Button>
        );
      })}
    </div>
  );
}

function CompareModal({ ids, onClose }: { ids: number[]; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["/api/farmway/comparar", ids.join(",")],
    queryFn: () => authFetch(`/api/farmway/comparar?ids=${ids.join(",")}`),
    enabled: ids.length >= 2,
  });

  if (!data && !isLoading) return null;

  const allSpecKeys = new Set<string>();
  data?.modelos?.forEach((m: any) => {
    if (m.specs) Object.keys(m.specs).forEach(k => { if (k !== "fonte") allSpecKeys.add(k); });
  });

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitCompare className="h-5 w-5" /> Comparação de Modelos</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 min-w-[140px]">Especificação</th>
                  {data?.modelos?.map((m: any) => (
                    <th key={m.id} className="text-left p-2 min-w-[160px]">
                      <div className="font-bold">{m.nome}</div>
                      <Badge className={`text-[10px] mt-0.5 ${FAB_COLORS[m.fabricante_slug] || ""}`}>{m.fabricante_nome}</Badge>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Potência (cv)", key: "_potencia_cv" },
                  { label: "Motor", key: "_motor" },
                  { label: "Transmissão", key: "_transmissao" },
                  { label: "Peso (kg)", key: "_peso_kg" },
                  { label: "Categoria", key: "_categoria" },
                ].map(row => (
                  <tr key={row.key} className="border-b hover:bg-muted/50">
                    <td className="p-2 font-medium text-muted-foreground">{row.label}</td>
                    {data?.modelos?.map((m: any) => (
                      <td key={m.id} className="p-2">
                        {row.key === "_potencia_cv" ? (m.potencia_cv ? `${m.potencia_cv} cv` : "—") :
                         row.key === "_motor" ? (m.motor || "—") :
                         row.key === "_transmissao" ? (m.transmissao || "—") :
                         row.key === "_peso_kg" ? (m.peso_kg ? `${m.peso_kg.toLocaleString("pt-BR")} kg` : "—") :
                         row.key === "_categoria" ? (m.categoria_nome || "—") : "—"}
                      </td>
                    ))}
                  </tr>
                ))}
                {[...allSpecKeys].sort().map(key => (
                  <tr key={key} className="border-b hover:bg-muted/50">
                    <td className="p-2 font-medium text-muted-foreground text-xs">{SPEC_LABELS[key] || key}</td>
                    {data?.modelos?.map((m: any) => (
                      <td key={m.id} className="p-2 text-xs">
                        {m.specs?.[key] !== undefined ? formatSpecValue(key, m.specs[key]) : "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailModal({ modelo, onClose }: { modelo: any; onClose: () => void }) {
  if (!modelo) return null;
  const specs = modelo.specs || {};
  const specEntries = Object.entries(specs).filter(([k]) => k !== "fonte");

  const keyGroups: Record<string, string[]> = {
    "Motor e Transmissão": ["cilindrada_l", "num_cilindros", "aspiracao", "rpm_potencia_nominal", "torque_max_nm", "reserva_torque_pct", "consumo_especifico_g_kwh"],
    "Hidráulico e TDP": ["vazao_hidraulica_lpm", "valvulas_remotas", "tomada_forca_rpm", "levante_3p_kg", "levante_dianteiro_kg"],
    "Tração e Dimensões": ["tipo_tracao", "tipo_direcao", "eixo_dianteiro", "bitola_min_m", "bitola_max_m", "comprimento_mm", "largura_mm", "altura_mm", "pneu_dianteiro", "pneu_traseiro", "pneu", "pressao_solo_kpa", "peso_lastro_max_kg"],
    "Cabine e Conforto": ["cabine", "ar_condicionado", "suspensao_cabine", "display", "monitor"],
    "Tecnologia": ["conectividade", "ipm", "potencia_ipm_cv", "epm", "potencia_epm_cv", "autotrac", "piloto_automatico", "starfire_receiver", "precisao_rtk_cm", "gps", "rtk", "isobus", "mapa_produtividade", "mapa_aplicacao", "controle_secao", "taxa_variavel", "combine_advisor", "active_yield", "sensor_semente", "monitor_semeadura", "controle_populacao"],
    "Tanques e Capacidade": ["tanque_combustivel_l", "tanque_graos_l", "tanque_calda_l", "tanque_agua_limpa_l", "capacidade_semente_l", "capacidade_adubo_kg", "capacidade_m3", "capacidade_kg", "capacidade_tanque_l", "capacidade_sacas_h", "mudas_por_dia"],
    "Desempenho": ["velocidade_max_kmh", "velocidade_descarga_lps", "rendimento_max_ton_h", "velocidade_colheita_kmh", "velocidade_trabalho_kmh"],
    "Implemento / Colheita": ["sistema_trilha", "area_peneiras_m2", "area_separacao_m2", "peneiras_3d", "largura_plataforma_max_m", "barra_max_m", "vao_livre_m", "exactapply", "see_spray", "aim_command", "controle_bico_a_bico", "sistema_bico", "vazao_max_lpm", "sistema_colheita", "facas_corte_base", "rolo_picador", "elevador"],
    "Plantio": ["linhas", "espacamento_cm", "sistema_dosagem", "tipo_dosagem", "singulacao_pct", "tipo_chassi", "ccs", "tipo_disco", "sistema_abertura_sulco", "controle_profundidade"],
    "Solo / Grade": ["diametro_disco_mm", "diametro_disco_pol", "profundidade_max_cm", "profundidade_min_cm", "potencia_requerida_cv", "peso_por_disco_kg", "hastes", "tipo_haste"],
    "Distribuição / Fardos": ["tipo_fardo", "dimensao_fardo_cm", "diametro_fardo_m", "diametro_min_m", "diametro_max_m", "sistema_amarre", "sistema_no", "tipo_camara", "embalagem_plastico", "largura_coleta_m", "sistema_espalhamento", "sistema_aplicacao", "correcao_automatica", "controle_bordadura", "pesagem_dinamica", "largura_aplicacao_m", "densidade_max_kg_m3"],
    "Misturador": ["roscas", "balanca", "descarga"],
    "Roçadeira / Outros": ["tipo_ferramenta", "secoes", "condicionador", "rotores", "turbina", "acionamento", "tipo_navalha", "alimentacao", "corte_rente"],
  };

  const usedKeys = new Set<string>();

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{modelo.nome}</span>
            <Badge className={`${FAB_COLORS[modelo.fabricante_slug] || ""}`}>{modelo.fabricante_nome}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {modelo.potencia_cv && (
            <Card><CardContent className="pt-3 pb-2 text-center">
              <Gauge className="h-4 w-4 mx-auto text-orange-500 mb-1" />
              <p className="text-lg font-bold">{modelo.potencia_cv} cv</p>
              <p className="text-[10px] text-muted-foreground">Potência</p>
            </CardContent></Card>
          )}
          {modelo.peso_kg && (
            <Card><CardContent className="pt-3 pb-2 text-center">
              <Weight className="h-4 w-4 mx-auto text-blue-500 mb-1" />
              <p className="text-lg font-bold">{modelo.peso_kg.toLocaleString("pt-BR")} kg</p>
              <p className="text-[10px] text-muted-foreground">Peso</p>
            </CardContent></Card>
          )}
          {specs.tanque_combustivel_l && (
            <Card><CardContent className="pt-3 pb-2 text-center">
              <Fuel className="h-4 w-4 mx-auto text-yellow-500 mb-1" />
              <p className="text-lg font-bold">{specs.tanque_combustivel_l} L</p>
              <p className="text-[10px] text-muted-foreground">Tanque</p>
            </CardContent></Card>
          )}
          {specs.vazao_hidraulica_lpm && (
            <Card><CardContent className="pt-3 pb-2 text-center">
              <Droplets className="h-4 w-4 mx-auto text-cyan-500 mb-1" />
              <p className="text-lg font-bold">{specs.vazao_hidraulica_lpm} L/min</p>
              <p className="text-[10px] text-muted-foreground">Vazão Hidr.</p>
            </CardContent></Card>
          )}
        </div>

        {modelo.motor && (
          <div className="bg-muted/50 rounded-lg p-3 mb-3">
            <p className="text-xs text-muted-foreground mb-0.5">Motor</p>
            <p className="text-sm font-medium">{modelo.motor}</p>
          </div>
        )}
        {modelo.transmissao && (
          <div className="bg-muted/50 rounded-lg p-3 mb-3">
            <p className="text-xs text-muted-foreground mb-0.5">Transmissão</p>
            <p className="text-sm font-medium">{modelo.transmissao}</p>
          </div>
        )}
        {modelo.descricao && <p className="text-sm text-muted-foreground mb-4">{modelo.descricao}</p>}

        {specs.aplicacoes && (
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-1.5">Aplicações</p>
            <div className="flex flex-wrap gap-1.5">
              {String(specs.aplicacoes).split(",").map((a: string) => (
                <Badge key={a.trim()} variant="secondary" className="text-xs">{a.trim()}</Badge>
              ))}
            </div>
          </div>
        )}

        {specEntries.length > 0 && (
          <div className="space-y-3">
            {Object.entries(keyGroups).map(([groupName, groupKeys]) => {
              const groupSpecs = groupKeys.filter(k => specs[k] !== undefined);
              groupSpecs.forEach(k => usedKeys.add(k));
              if (groupSpecs.length === 0) return null;
              return (
                <div key={groupName}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wider">{groupName}</p>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {groupSpecs.map((key, idx) => (
                          <tr key={key} className={idx % 2 === 0 ? "bg-muted/30" : ""}>
                            <td className="p-2 text-muted-foreground font-medium w-1/2">{SPEC_LABELS[key] || key}</td>
                            <td className="p-2">{formatSpecValue(key, specs[key])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
            {(() => {
              const remaining = specEntries.filter(([k]) => !usedKeys.has(k) && k !== "aplicacoes");
              if (remaining.length === 0) return null;
              return (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wider">Outros</p>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {remaining.map(([key, value], idx) => (
                          <tr key={key} className={idx % 2 === 0 ? "bg-muted/30" : ""}>
                            <td className="p-2 text-muted-foreground font-medium w-1/2">{SPEC_LABELS[key] || key}</td>
                            <td className="p-2">{formatSpecValue(key, value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {modelo.similares?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Modelos Similares</p>
            <div className="flex flex-wrap gap-2">
              {modelo.similares.map((s: any) => (
                <Badge key={s.id} variant="outline" className="text-xs py-1 px-2">
                  {s.fabricante_nome} {s.nome} {s.potencia_cv ? `(${s.potencia_cv}cv)` : ""}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {modelo.url_origem && (
          <div className="mt-4 text-right">
            <a href={modelo.url_origem} target="_blank" rel="noopener" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Catálogo oficial
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModelosTable() {
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fabricantes_sel, setFabricantesSel] = useState<string[]>([]);
  const [categorias_sel, setCategoriasSel] = useState<string[]>([]);
  const [serie, setSerie] = useState("");
  const [potenciaMin, setPotenciaMin] = useState("");
  const [potenciaMax, setPotenciaMax] = useState("");
  const [pesoMin, setPesoMin] = useState("");
  const [pesoMax, setPesoMax] = useState("");
  const [motorFilter, setMotorFilter] = useState("");
  const [transmissaoFilter, setTransmissaoFilter] = useState("");
  const [aplicacaoFilter, setAplicacaoFilter] = useState("");
  const [tracaoFilter, setTracaoFilter] = useState("");
  const [aspiracaoFilter, setAspiracaoFilter] = useState("");
  const [paisOrigem, setPaisOrigem] = useState("");
  const [comMotor, setComMotor] = useState(false);
  const [comPeso, setComPeso] = useState(false);
  const [comSpecs, setComSpecs] = useState(false);
  const [comCabine, setComCabine] = useState(false);
  const [comConectividade, setComConectividade] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortBy, setSortBy] = useState("nome");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedModelo, setSelectedModelo] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [showCompare, setShowCompare] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");

  const fabricante = fabricantes_sel.join(",");
  const categoria = categorias_sel.join(",");

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.set("limit", limit.toString());
    params.set("offset", (page * limit).toString());
    params.set("sort_by", sortBy);
    params.set("sort_order", sortOrder);
    if (search) params.set("search", search);
    if (fabricante) params.set("fabricante", fabricante);
    if (categoria) params.set("categoria", categoria);
    if (serie) params.set("serie", serie);
    if (potenciaMin) params.set("potencia_min", potenciaMin);
    if (potenciaMax) params.set("potencia_max", potenciaMax);
    if (pesoMin) params.set("peso_min", pesoMin);
    if (pesoMax) params.set("peso_max", pesoMax);
    if (motorFilter) params.set("motor", motorFilter);
    if (transmissaoFilter) params.set("transmissao", transmissaoFilter);
    if (aplicacaoFilter) params.set("aplicacao", aplicacaoFilter);
    if (tracaoFilter) params.set("tipo_tracao", tracaoFilter);
    if (aspiracaoFilter) params.set("aspiracao", aspiracaoFilter);
    if (comMotor) params.set("com_motor", "true");
    if (comPeso) params.set("com_peso", "true");
    if (comSpecs) params.set("com_specs", "true");
    if (comCabine) params.set("com_cabine", "true");
    if (comConectividade) params.set("conectividade", "true");
    return params.toString();
  };

  const allFilters = [search, fabricante, categoria, serie, potenciaMin, potenciaMax, pesoMin, pesoMax, motorFilter, transmissaoFilter, aplicacaoFilter, tracaoFilter, aspiracaoFilter, paisOrigem, comMotor, comPeso, comSpecs, comCabine, comConectividade];

  const { data, isLoading } = useQuery({
    queryKey: ["/api/farmway/modelos", ...allFilters, page, limit, sortBy, sortOrder],
    queryFn: () => authFetch(`/api/farmway/modelos?${buildQueryString()}`),
  });

  const { data: fabricantes } = useQuery({
    queryKey: ["/api/farmway/fabricantes"],
    queryFn: () => authFetch("/api/farmway/fabricantes"),
  });

  const { data: categorias } = useQuery({
    queryKey: ["/api/farmway/categorias"],
    queryFn: () => authFetch("/api/farmway/categorias"),
  });

  const { data: series } = useQuery({
    queryKey: ["/api/farmway/series", fabricante, categoria],
    queryFn: () => authFetch(`/api/farmway/series?${fabricante ? `fabricante=${fabricante}` : ""}${categoria ? `&categoria=${categoria}` : ""}`),
  });

  const { data: filtrosData } = useQuery({
    queryKey: ["/api/farmway/filtros"],
    queryFn: () => authFetch("/api/farmway/filtros"),
  });

  const toggleFabricante = (slug: string) => {
    setFabricantesSel(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
    setSerie(""); setPage(0);
  };

  const toggleCategoria = (slug: string) => {
    setCategoriasSel(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
    setSerie(""); setPage(0);
  };

  const filteredFabricantes = paisOrigem
    ? (fabricantes || []).filter((f: any) => f.pais_origem === paisOrigem)
    : fabricantes;

  const handleSort = (col: string) => {
    if (sortBy === col) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortOrder("asc"); }
    setPage(0);
  };

  const handleSearch = () => { setSearch(searchInput); setPage(0); };

  const clearFilters = () => {
    setSearch(""); setSearchInput(""); setFabricantesSel([]); setCategoriasSel([]); setSerie("");
    setPotenciaMin(""); setPotenciaMax(""); setPesoMin(""); setPesoMax("");
    setMotorFilter(""); setTransmissaoFilter(""); setAplicacaoFilter(""); setTracaoFilter(""); setAspiracaoFilter("");
    setPaisOrigem("");
    setComMotor(false); setComPeso(false); setComSpecs(false); setComCabine(false); setComConectividade(false);
    setPage(0);
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const detail = await authFetch(`/api/farmway/modelos/${id}`);
      setSelectedModelo(detail);
    } catch { setSelectedModelo(null); } finally { setDetailLoading(false); }
  };

  const toggleCompare = (id: number) => {
    setCompareIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 6 ? [...prev, id] : prev);
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 inline ml-0.5 opacity-30" />;
    return sortOrder === "asc" ? <ChevronUp className="h-3 w-3 inline ml-0.5" /> : <ChevronDown className="h-3 w-3 inline ml-0.5" />;
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;
  const hasFilters = allFilters.some(Boolean);
  const activeFilterCount = allFilters.filter(Boolean).length;

  const paises = [...new Set((fabricantes || []).map((f: any) => f.pais_origem).filter(Boolean))] as string[];

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Factory className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Fabricantes</span>
            {fabricantes_sel.length > 0 && (
              <Badge variant="secondary" className="text-xs">{fabricantes_sel.length} selecionado(s)</Badge>
            )}
          </div>
          {paises.length > 1 && (
            <Select value={paisOrigem || "_all"} onValueChange={(v) => { setPaisOrigem(v === "_all" ? "" : v); setFabricantesSel([]); setPage(0); }}>
              <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="select-farmway-pais">
                <Globe className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Todos os países" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Todos os países</SelectItem>
                {paises.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        </div>
        <FabricantesGrid
          fabricantes={filteredFabricantes || []}
          onSelect={toggleFabricante}
          selected={fabricantes_sel}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Tipo de Equipamento</span>
          {categorias_sel.length > 0 && (
            <Badge variant="secondary" className="text-xs">{categorias_sel.length} tipo(s)</Badge>
          )}
        </div>
        <CategoriasChips
          categorias={categorias || []}
          selected={categorias_sel}
          onSelect={toggleCategoria}
        />
      </div>

      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs mb-1 block">Buscar</Label>
              <div className="flex gap-1.5">
                <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Modelo, série, motor, transmissão..." onKeyDown={(e) => e.key === "Enter" && handleSearch()} data-testid="input-farmway-search" />
                <Button onClick={handleSearch} size="icon" variant="secondary" data-testid="button-farmway-search"><Search className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">Série</Label>
              <Select value={serie || "_all"} onValueChange={(v) => { setSerie(v === "_all" ? "" : v); setPage(0); }}>
                <SelectTrigger data-testid="select-farmway-serie"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas</SelectItem>
                  {series?.map((s: any) => (<SelectItem key={s.serie} value={s.serie}>{s.serie} ({s.total})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[75px]">
              <Label className="text-xs mb-1 block">CV mín</Label>
              <Input type="number" value={potenciaMin} onChange={(e) => { setPotenciaMin(e.target.value); setPage(0); }} placeholder={filtrosData?.potencia?.min ? String(Math.floor(filtrosData.potencia.min)) : "0"} data-testid="input-farmway-potencia-min" />
            </div>
            <div className="min-w-[75px]">
              <Label className="text-xs mb-1 block">CV máx</Label>
              <Input type="number" value={potenciaMax} onChange={(e) => { setPotenciaMax(e.target.value); setPage(0); }} placeholder={filtrosData?.potencia?.max ? String(Math.ceil(filtrosData.potencia.max)) : "999"} data-testid="input-farmway-potencia-max" />
            </div>

            {filtrosData?.aspiracoes?.length > 0 && (
              <div className="min-w-[130px]">
                <Label className="text-xs mb-1 block">Aspiração</Label>
                <Select value={aspiracaoFilter || "_all"} onValueChange={(v) => { setAspiracaoFilter(v === "_all" ? "" : v); setPage(0); }}>
                  <SelectTrigger data-testid="select-farmway-aspiracao"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todas</SelectItem>
                    {filtrosData.aspiracoes.map((a: any) => (<SelectItem key={a.valor} value={a.valor}>{a.valor} ({a.total})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {filtrosData?.tipos_tracao?.length > 0 && (
              <div className="min-w-[130px]">
                <Label className="text-xs mb-1 block">Tração</Label>
                <Select value={tracaoFilter || "_all"} onValueChange={(v) => { setTracaoFilter(v === "_all" ? "" : v); setPage(0); }}>
                  <SelectTrigger data-testid="select-farmway-tracao"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todas</SelectItem>
                    {filtrosData.tipos_tracao.map((t: any) => (<SelectItem key={t.valor} value={t.valor}>{t.valor} ({t.total})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button variant={showAdvanced ? "secondary" : "outline"} size="sm" onClick={() => setShowAdvanced(!showAdvanced)} className="gap-1.5" data-testid="button-farmway-advanced-filters">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              + Filtros
              {activeFilterCount > 4 && <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">{activeFilterCount}</Badge>}
            </Button>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1" data-testid="button-farmway-clear-filters">
                <RotateCcw className="h-3.5 w-3.5" /> Limpar ({activeFilterCount})
              </Button>
            )}
          </div>

          {showAdvanced && (
            <div className="mt-4 pt-4 border-t space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div>
                  <Label className="text-xs mb-1 block">Peso mín (kg)</Label>
                  <Input type="number" value={pesoMin} onChange={(e) => { setPesoMin(e.target.value); setPage(0); }} placeholder={filtrosData?.peso?.min ? String(Math.floor(filtrosData.peso.min)) : "0"} data-testid="input-farmway-peso-min" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Peso máx (kg)</Label>
                  <Input type="number" value={pesoMax} onChange={(e) => { setPesoMax(e.target.value); setPage(0); }} placeholder={filtrosData?.peso?.max ? String(Math.ceil(filtrosData.peso.max)) : "25000"} data-testid="input-farmway-peso-max" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Motor contém</Label>
                  <Input value={motorFilter} onChange={(e) => { setMotorFilter(e.target.value); setPage(0); }} placeholder="FPT, Cummins, AGCO..." data-testid="input-farmway-motor" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Transmissão contém</Label>
                  <Input value={transmissaoFilter} onChange={(e) => { setTransmissaoFilter(e.target.value); setPage(0); }} placeholder="CVT, Powershift..." data-testid="input-farmway-transmissao" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Aplicação contém</Label>
                  <Input value={aplicacaoFilter} onChange={(e) => { setAplicacaoFilter(e.target.value); setPage(0); }} placeholder="Soja, Café, Cana..." data-testid="input-farmway-aplicacao" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Ordenar por</Label>
                  <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(0); }}>
                    <SelectTrigger data-testid="select-farmway-sort"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nome">Nome</SelectItem>
                      <SelectItem value="potencia_cv">Potência (CV)</SelectItem>
                      <SelectItem value="peso_kg">Peso (kg)</SelectItem>
                      <SelectItem value="fabricante">Fabricante</SelectItem>
                      <SelectItem value="categoria">Categoria</SelectItem>
                      <SelectItem value="atualizado_em">Última atualização</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filtrosData?.aplicacoes?.length > 0 && (
                <div>
                  <Label className="text-xs mb-1.5 block text-muted-foreground">Filtros rápidos por aplicação</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {filtrosData.aplicacoes.slice(0, 12).map((a: any) => (
                      <Button
                        key={a.valor}
                        variant={aplicacaoFilter === a.valor ? "default" : "outline"}
                        size="sm"
                        className="h-6 text-[11px] gap-1"
                        onClick={() => { setAplicacaoFilter(aplicacaoFilter === a.valor ? "" : a.valor); setPage(0); }}
                        data-testid={`chip-app-${a.valor}`}
                      >
                        <Sprout className="h-2.5 w-2.5" /> {a.valor} <span className="opacity-60">({a.total})</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-x-5 gap-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox id="comMotor" checked={comMotor} onCheckedChange={(v) => { setComMotor(!!v); setPage(0); }} data-testid="checkbox-farmway-com-motor" />
                  <Label htmlFor="comMotor" className="text-xs cursor-pointer flex items-center gap-1"><Cog className="h-3 w-3" /> Só com motor</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="comPeso" checked={comPeso} onCheckedChange={(v) => { setComPeso(!!v); setPage(0); }} data-testid="checkbox-farmway-com-peso" />
                  <Label htmlFor="comPeso" className="text-xs cursor-pointer flex items-center gap-1"><Weight className="h-3 w-3" /> Só com peso</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="comSpecs" checked={comSpecs} onCheckedChange={(v) => { setComSpecs(!!v); setPage(0); }} data-testid="checkbox-farmway-com-specs" />
                  <Label htmlFor="comSpecs" className="text-xs cursor-pointer flex items-center gap-1"><Settings2 className="h-3 w-3" /> Com specs detalhados</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="comCabine" checked={comCabine} onCheckedChange={(v) => { setComCabine(!!v); setPage(0); }} data-testid="checkbox-farmway-com-cabine" />
                  <Label htmlFor="comCabine" className="text-xs cursor-pointer flex items-center gap-1"><Thermometer className="h-3 w-3" /> Com cabine</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="comConectividade" checked={comConectividade} onCheckedChange={(v) => { setComConectividade(!!v); setPage(0); }} data-testid="checkbox-farmway-com-conectividade" />
                  <Label htmlFor="comConectividade" className="text-xs cursor-pointer flex items-center gap-1"><Wifi className="h-3 w-3" /> Com conectividade</Label>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {hasFilters && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground mr-1">Filtros ativos:</span>
          {fabricantes_sel.map(f => (
            <Badge key={f} variant="secondary" className={`text-[10px] gap-1 cursor-pointer ${FAB_COLORS[f] || ""}`} onClick={() => toggleFabricante(f)}>
              {f} <X className="h-2.5 w-2.5" />
            </Badge>
          ))}
          {categorias_sel.map(c => (
            <Badge key={c} variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => toggleCategoria(c)}>
              {c} <X className="h-2.5 w-2.5" />
            </Badge>
          ))}
          {search && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => { setSearch(""); setSearchInput(""); }}>busca: {search} <X className="h-2.5 w-2.5" /></Badge>}
          {potenciaMin && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setPotenciaMin("")}>≥{potenciaMin}cv <X className="h-2.5 w-2.5" /></Badge>}
          {potenciaMax && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setPotenciaMax("")}>≤{potenciaMax}cv <X className="h-2.5 w-2.5" /></Badge>}
          {aspiracaoFilter && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setAspiracaoFilter("")}>{aspiracaoFilter} <X className="h-2.5 w-2.5" /></Badge>}
          {tracaoFilter && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setTracaoFilter("")}>{tracaoFilter} <X className="h-2.5 w-2.5" /></Badge>}
          {aplicacaoFilter && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setAplicacaoFilter("")}>{aplicacaoFilter} <X className="h-2.5 w-2.5" /></Badge>}
          {serie && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setSerie("")}>série: {serie} <X className="h-2.5 w-2.5" /></Badge>}
          {motorFilter && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setMotorFilter("")}>motor: {motorFilter} <X className="h-2.5 w-2.5" /></Badge>}
          {comCabine && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setComCabine(false)}>com cabine <X className="h-2.5 w-2.5" /></Badge>}
          {comConectividade && <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => setComConectividade(false)}>com conectividade <X className="h-2.5 w-2.5" /></Badge>}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {compareIds.length >= 2 && (
            <>
              <Button size="sm" onClick={() => setShowCompare(true)} className="gap-1.5" data-testid="button-farmway-compare">
                <GitCompare className="h-3.5 w-3.5" /> Comparar {compareIds.length} modelos
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCompareIds([])} data-testid="button-farmway-compare-clear">
                <X className="h-3.5 w-3.5" /> Limpar seleção
              </Button>
            </>
          )}
          <span className="text-xs text-muted-foreground">{data?.total || 0} modelos encontrados</span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant={viewMode === "table" ? "secondary" : "ghost"} className="h-7 w-7" onClick={() => setViewMode("table")} data-testid="button-view-table">
            <List className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant={viewMode === "grid" ? "secondary" : "ghost"} className="h-7 w-7" onClick={() => setViewMode("grid")} data-testid="button-view-grid">
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {isLoading ? (
            <div className="col-span-full flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : data?.modelos?.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">Nenhum modelo encontrado com os filtros atuais</div>
          ) : data?.modelos?.map((m: any) => (
            <Card key={m.id} className={`cursor-pointer hover:shadow-md transition-all border ${compareIds.includes(m.id) ? "ring-2 ring-primary" : ""}`} onClick={() => openDetail(m.id)} data-testid={`card-modelo-${m.id}`}>
              <CardContent className="pt-4 pb-3 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm leading-tight">{m.nome}</p>
                    <Badge variant="secondary" className={`text-[10px] mt-1 ${FAB_COLORS[m.fabricante_slug] || ""}`}>{m.fabricante_nome}</Badge>
                  </div>
                  <Checkbox checked={compareIds.includes(m.id)} onCheckedChange={(e) => { e; toggleCompare(m.id); }} onClick={(e) => e.stopPropagation()} data-testid={`checkbox-compare-grid-${m.id}`} />
                </div>
                <Badge variant="outline" className="text-[10px]">{m.categoria_nome}</Badge>
                {m.serie && <span className="text-[10px] text-muted-foreground ml-1">• {m.serie}</span>}
                <div className="flex flex-wrap gap-2 pt-1">
                  {m.potencia_cv && (
                    <div className="flex items-center gap-1 text-xs">
                      <Gauge className="h-3 w-3 text-orange-500" />
                      <span className="font-mono font-medium">{m.potencia_cv} cv</span>
                    </div>
                  )}
                  {m.peso_kg && (
                    <div className="flex items-center gap-1 text-xs">
                      <Weight className="h-3 w-3 text-blue-500" />
                      <span className="font-mono">{m.peso_kg.toLocaleString("pt-BR")} kg</span>
                    </div>
                  )}
                  {m.specs?.tipo_tracao && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Cog className="h-3 w-3" /> {m.specs.tipo_tracao}
                    </div>
                  )}
                </div>
                {m.motor && <p className="text-[11px] text-muted-foreground truncate" title={m.motor}>{m.motor}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="table-farmway-modelos">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-2 w-8"><Checkbox checked={false} disabled className="opacity-30" /></th>
                      <th className="text-left p-2 cursor-pointer hover:text-primary" onClick={() => handleSort("nome")}>Modelo <SortIcon col="nome" /></th>
                      <th className="text-left p-2 cursor-pointer hover:text-primary" onClick={() => handleSort("fabricante")}>Fabricante <SortIcon col="fabricante" /></th>
                      <th className="text-left p-2 cursor-pointer hover:text-primary" onClick={() => handleSort("categoria")}>Categoria <SortIcon col="categoria" /></th>
                      <th className="text-left p-2 cursor-pointer hover:text-primary hidden md:table-cell" onClick={() => handleSort("serie")}>Série <SortIcon col="serie" /></th>
                      <th className="text-right p-2 cursor-pointer hover:text-primary" onClick={() => handleSort("potencia_cv")}>CV <SortIcon col="potencia_cv" /></th>
                      <th className="text-left p-2 hidden lg:table-cell cursor-pointer hover:text-primary" onClick={() => handleSort("motor")}>Motor <SortIcon col="motor" /></th>
                      <th className="text-left p-2 hidden xl:table-cell cursor-pointer hover:text-primary" onClick={() => handleSort("transmissao")}>Transmissão <SortIcon col="transmissao" /></th>
                      <th className="text-right p-2 hidden md:table-cell cursor-pointer hover:text-primary" onClick={() => handleSort("peso_kg")}>Peso <SortIcon col="peso_kg" /></th>
                      <th className="p-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.modelos?.map((m: any) => (
                      <tr key={m.id} className={`border-b hover:bg-muted/30 cursor-pointer ${compareIds.includes(m.id) ? "bg-primary/5" : ""}`} data-testid={`row-modelo-${m.id}`}>
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={compareIds.includes(m.id)} onCheckedChange={() => toggleCompare(m.id)} data-testid={`checkbox-compare-${m.id}`} />
                        </td>
                        <td className="p-2 font-medium" onClick={() => openDetail(m.id)}>{m.nome}</td>
                        <td className="p-2" onClick={() => openDetail(m.id)}>
                          <Badge variant="secondary" className={`text-[10px] ${FAB_COLORS[m.fabricante_slug] || ""}`}>{m.fabricante_nome}</Badge>
                        </td>
                        <td className="p-2 text-muted-foreground text-xs" onClick={() => openDetail(m.id)}>{m.categoria_nome}</td>
                        <td className="p-2 text-xs hidden md:table-cell" onClick={() => openDetail(m.id)}>{m.serie || "—"}</td>
                        <td className="p-2 text-right font-mono" onClick={() => openDetail(m.id)}>{m.potencia_cv || "—"}</td>
                        <td className="p-2 text-xs hidden lg:table-cell max-w-[200px] truncate" onClick={() => openDetail(m.id)} title={m.motor}>{m.motor || "—"}</td>
                        <td className="p-2 text-xs hidden xl:table-cell max-w-[180px] truncate" onClick={() => openDetail(m.id)} title={m.transmissao}>{m.transmissao || "—"}</td>
                        <td className="p-2 text-right font-mono hidden md:table-cell" onClick={() => openDetail(m.id)}>{m.peso_kg ? `${m.peso_kg.toLocaleString("pt-BR")}` : "—"}</td>
                        <td className="p-2">
                          <Button size="icon" variant="ghost" onClick={() => openDetail(m.id)} data-testid={`button-detail-${m.id}`}>
                            <Info className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {data?.modelos?.length === 0 && (
                      <tr><td colSpan={10} className="text-center py-12 text-muted-foreground">Nenhum modelo encontrado com os filtros atuais</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{data?.total || 0} modelos • Página {page + 1} de {totalPages || 1}</p>
        <div className="flex gap-1">
          <Button size="icon" variant="outline" className="h-7 w-7" disabled={page === 0} onClick={() => setPage(0)} data-testid="button-farmway-first-page">
            <ChevronLeft className="h-3 w-3" /><ChevronLeft className="h-3 w-3 -ml-2" />
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="button-farmway-prev-page">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} data-testid="button-farmway-next-page">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)} data-testid="button-farmway-last-page">
            <ChevronRight className="h-3 w-3" /><ChevronRight className="h-3 w-3 -ml-2" />
          </Button>
        </div>
      </div>

      {showCompare && <CompareModal ids={compareIds} onClose={() => setShowCompare(false)} />}
      {selectedModelo && <DetailModal modelo={selectedModelo} onClose={() => setSelectedModelo(null)} />}
      {detailLoading && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>
      )}
    </div>
  );
}

export default function FarmWayPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const { data: stats, isLoading: loadingStats, isError: statsError } = useQuery({
    queryKey: ["/api/farmway/stats"],
    queryFn: () => authFetch("/api/farmway/stats"),
    enabled: isAuthenticated,
  });

  const scrapeMutation = useMutation({
    mutationFn: () => authFetch("/api/farmway/scrape", { method: "POST" }),
    onSuccess: () => {
      toast({ title: "Importação Iniciada", description: "Os dados estão sendo atualizados em background." });
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["/api/farmway/stats"] }), 5000);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/farmway/stats"] });
        queryClient.invalidateQueries({ queryKey: ["/api/farmway/fabricantes"] });
        queryClient.invalidateQueries({ queryKey: ["/api/farmway/modelos"] });
      }, 15000);
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  if (!isAuthenticated) {
    return (
      <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
        <ModuleHeader
          icon={Tractor}
          title="FarmWay - Equipamentos Agrícolas"
          description="Base de dados de máquinas e implementos com especificações técnicas completas de catálogo"
        />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <Tractor className="h-12 w-12 text-muted-foreground/40" />
            <p className="text-lg font-medium text-muted-foreground" data-testid="text-farmway-auth-required">Faça login para acessar o catálogo FarmWay</p>
            <p className="text-sm text-muted-foreground/70">Utilize suas credenciais para visualizar os dados de equipamentos.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <ModuleHeader
        icon={Tractor}
        title="FarmWay - Equipamentos Agrícolas"
        description="Base de dados de máquinas e implementos com especificações técnicas completas de catálogo"
      />

      <div className="flex items-center gap-3">
        <Button onClick={() => scrapeMutation.mutate()} disabled={scrapeMutation.isPending || stats?.scraping} className="gap-2" data-testid="button-farmway-scrape">
          {scrapeMutation.isPending || stats?.scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {stats?.scraping ? "Importando..." : "Atualizar Dados"}
        </Button>
        {stats?.scraping && <Badge variant="secondary" className="animate-pulse">Scraping em andamento</Badge>}
      </div>

      {loadingStats ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : (
        <StatsCards stats={stats} />
      )}

      <ModelosTable />
    </div>
  );
}
